import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import ReactDOM from "react-dom";
import { useLocation } from "react-router-dom";
import {
  FileText, Plus, X, Loader2, Copy, Trash2, Download,
  Send, Eye, Edit3, BookOpen, Upload, Search,
  CheckCircle2, XCircle, Clock, RefreshCw, Percent,
  Building2, Hammer, FileSpreadsheet, ChevronRight, ChevronDown,
  AlertTriangle, History, ArrowRight, ListChecks,
  Ban, CalendarDays, Paperclip, FilePlus, Archive,
  RotateCcw, Users, TrendingUp, Target,
  Sparkles, BarChart3, PieChart as PieChartIcon, Wallet, Printer
} from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, Legend } from "recharts";
import axios from "axios";
import { API, useAuth } from "../../contexts/AuthContext";
import { formatPYG } from "../../utils/format";
import { Skeleton } from "../../components/ui/skeleton";
import { toast } from "sonner";
import { WORKFLOW_STEPS, STATUS_META, NEXT_STEP_LABEL, TIPO_META, UNIDADES, DEFAULT_PCT, opts, fmt } from "./constants";
import { Modal, DeadlineBadge, WorkflowStepper, daysUntil, getWfIndex } from "./SharedComponents";
import { ResumenFinancieroSection } from "./ResumenFinancieroSection";
import { GastosAdminSection } from "./GastosAdminSection";
import { HorasHombreSection } from "./HorasHombreSection";
import { SimuladorCurvaPersonal } from "./SimuladorCurvaPersonal";   // Fix HF
import { computeBudgetLive } from "./budgetCalcs";
import { pdfAnalisisFinanciero } from "../../utils/analisisFinancieroPdf";   // Fix HE

const fmtGs = (n) => `Gs. ${Math.round(Number(n) || 0).toLocaleString("es-PY")}`;

// Fix HE (2026-10-20): tortas nuevas del Análisis Financiero (costo directo y ganancias).
function TortaCard({ testid, titulo, subtitulo, datos, vacio, sinAnimacion = false }) {
  const total = datos.reduce((sum, d) => sum + d.value, 0);
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-1">
        <PieChartIcon size={14} className="text-[#2E6DA4]" />
        <div>
          <h3 className="text-sm font-bold text-gray-800">{titulo}</h3>
          <p className="text-[10px] text-gray-400">{subtitulo}</p>
        </div>
      </div>
      {total <= 0 ? (
        <div className="text-center py-12 text-xs text-gray-400">{vacio}</div>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie
                isAnimationActive={!sinAnimacion}
                data={datos}
                dataKey="value"
                nameKey="name"
                innerRadius={50}
                outerRadius={90}
                paddingAngle={2}
                label={(entry) => `${((entry.value / total) * 100).toFixed(0)}%`}
                labelLine={false}
              >
                {datos.map((entry, i) => <Cell key={i} fill={entry.color} />)}
              </Pie>
              <Tooltip formatter={(value) => fmtGs(value)} />
            </PieChart>
          </ResponsiveContainer>
          <div className="grid grid-cols-1 gap-1.5 mt-2">
            {datos.map((d, i) => (
              <div key={i} className="flex items-center gap-1.5 text-xs">
                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: d.color }} />
                <span className="text-gray-600 truncate flex-1">{d.name}</span>
                <span className="text-gray-400 tabular-nums">{((d.value / total) * 100).toFixed(1)}%</span>
                <span className="text-gray-700 font-semibold tabular-nums">{fmtGs(d.value)}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export function FinancialsTab({ budgetId, items, pct, readOnly = false, budget = null, precioInputMode = "sin_iva", descuento = null }) {
  // PDF REV1 A.2 / A.3: helper compartido — respeta % por grupo (override parcial)
  // y replica EXACTAMENTE la misma fórmula que ResumenFinancieroSection y ItemRow.
  const live = useMemo(
    () => {
      const r = computeBudgetLive(items, pct, precioInputMode, descuento);
      // Mantenemos los nombres de campos legacy (subtotal en vez de subtotal_neto) para
      // no romper consumidores existentes en este archivo.
      return { ...r, subtotal: r.subtotal_neto };
    },
    [items, pct, precioInputMode, descuento]
  );

  const fmtPyg = (n) => `Gs. ${Math.round(Number(n) || 0).toLocaleString("es-PY")}`;

  const distribution = [
    { name: "Materiales (Directo)", value: live.mat_directo, color: "#2E6DA4" },
    { name: "Mano de Obra (Directo)", value: live.mo_directo, color: "#F59E0B" },
    { name: "Utilidades (G.M + G.MO)", value: live.utilidades, color: "#10B981" },
    { name: "Gastos Admin. (G.A)", value: live.ga, color: "#8B5CF6" },
  ].filter(d => d.value > 0);

  // Fix HE (2026-10-20): solo la inversión física (100% = Costo Directo) y solo las ganancias (100% = Utilidades, sin G.A.).
  const distribucionDirecta = [
    { name: "Materiales Directos", value: live.mat_directo, color: "#2E6DA4" },
    { name: "Mano de Obra Directa", value: live.mo_directo, color: "#F59E0B" },
  ].filter(d => d.value > 0);
  const distribucionGanancias = [
    { name: "Ganancia de Materiales (G.M)", value: live.gm, color: "#059669" },
    { name: "Ganancia de Mano de Obra (G.MO)", value: live.gmo, color: "#34D399" },
  ].filter(d => d.value > 0);

  const sectionData = live.bySection
    .filter(s => (s.mat + s.mo + s.gm + s.ga + s.gmo) > 0)
    .map(s => ({
      name: (s.numero || "") + " " + (s.nombre.length > 28 ? s.nombre.slice(0, 26) + "…" : s.nombre),
      Materiales: Math.round(s.mat),
      "Mano de Obra": Math.round(s.mo),
      Utilidades: Math.round(s.gm + s.gmo),
      "G.A.": Math.round(s.ga),
    }));

  // AI Analysis state
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [aiError, setAiError] = useState("");

  // PDF REV1 A.1: snapshot LIVE de Horas Hombre — sincroniza HorasHombreSection con
  // GastosAdminDetalladoSection en tiempo real (sin requerir guardado).
  const [hhLive, setHhLive] = useState(null);
  const handleHHLiveChange = useCallback((snapshot) => setHhLive(snapshot), []);

  // Fix HE (2026-10-20): imprimir en PDF todo lo que se ve en el Análisis Financiero.
  const { user } = useAuth();
  const raizRef = useRef(null);
  const [imprimiendo, setImprimiendo] = useState(false);
  const exportacionRef = useRef(null);
  const montadoRef = useRef(true);
  const progresoRef = useRef(null);
  useEffect(() => {
    montadoRef.current = true;
    setImprimiendo(false);
    return () => {
      montadoRef.current = false;
      exportacionRef.current?.abort();
      exportacionRef.current = null;
    };
  }, [budgetId]);
  const imprimirPdf = async () => {
    if (!raizRef.current || exportacionRef.current) return;
    const controller = new AbortController();
    exportacionRef.current = controller;
    setImprimiendo(true);
    try {
      const bloques = ["financials-kpis", ...(live.descuento_activo ? ["financials-descuento-banner"] : []), "financials-charts"];
      if (aiResult) bloques.push("ai-analysis-section");
      bloques.push("resumen-financiero-section", "gastos-admin-section", "horas-hombre-section", "simulador-curva-personal");   // Fix HF: también el simulador de curva de personal
      const nombre = (budget && (budget.nombre || budget.name)) || "Presupuesto";
      const numero = (budget && budget.number) || "";
      await pdfAnalisisFinanciero({
        raiz: raizRef.current,
        selectores: bloques,
        titulo: "Análisis Financiero",
        lineas: [
          `${nombre}${numero ? ` · N° ${numero}` : ""}`,
          [budget && budget.cliente ? `Cliente: ${budget.cliente}` : "", budget && budget.obra ? `Obra: ${budget.obra}` : ""].filter(Boolean).join(" · "),
          `Impreso el ${new Date().toLocaleDateString("es-PY")}${user && user.name ? ` por ${user.name}` : ""}`,
        ],
        archivo: `Analisis-Financiero-${numero || nombre}.pdf`,
        signal: controller.signal,
        onProgress: ({ fase, bloque, total, pagina }) => {
          if (exportacionRef.current !== controller || !montadoRef.current || !progresoRef.current) return;
          // El progreso no vuelve a renderizar gráficos ni secciones por cada página.
          progresoRef.current.textContent = fase === "capturando"
            ? `Generando PDF · sección ${bloque} de ${total} · página ${pagina}`
            : fase === "listo" ? "PDF listo" : "Preparando el informe...";
        },
      });
    } catch (err) {
      if (err.name !== "AbortError" && montadoRef.current && exportacionRef.current === controller) toast.error(err.message || "No se pudo generar el PDF");
    } finally {
      if (exportacionRef.current === controller) {
        exportacionRef.current = null;
        if (montadoRef.current) setImprimiendo(false);
      }
    }
  };

  const runAiAnalysis = async () => {
    if (!budgetId) {
      setAiError("Guardá el presupuesto primero antes de analizarlo con IA");
      return;
    }
    setAiLoading(true);
    setAiError("");
    setAiResult(null);
    try {
      const res = await axios.post(`${API}/budgets/${budgetId}/analyze-financials`, {}, { withCredentials: true });
      setAiResult(res.data);
    } catch (err) {
      setAiError(err.response?.data?.detail || "Error al analizar con IA");
    } finally {
      setAiLoading(false);
    }
  };

  if (live.costo_directo === 0) {
    return (
      <div className="flex-1 flex items-center justify-center p-8" data-testid="financials-empty">
        <div className="text-center">
          <BarChart3 size={36} className="mx-auto mb-3 text-gray-300" />
          <p className="text-sm text-gray-500">Aún no hay datos para analizar</p>
          <p className="text-xs text-gray-400 mt-1">Agregá ítems con costos en la pestaña "Estructura de Cómputo"</p>
        </div>
      </div>
    );
  }

  return (
    <div ref={raizRef} className="flex-1 overflow-auto p-6 space-y-5 bg-gray-50" data-testid="financials-tab">
      {/* Fix HE (2026-10-20): imprimir en PDF todo lo visible */}
      <div className="flex items-center justify-end gap-3" data-pdf-ignore>
        {imprimiendo && <>
          <span ref={progresoRef} role="status" className="text-xs text-gray-500">Preparando el informe...</span>
          <button type="button" onClick={() => exportacionRef.current?.abort()} className="text-xs text-gray-600 underline" data-testid="financials-print-cancel">Cancelar</button>
        </>}
        <button
          onClick={imprimirPdf}
          disabled={imprimiendo}
          className="flex items-center gap-2 border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 px-3 py-1.5 rounded-lg text-xs font-semibold disabled:opacity-60 transition-colors"
          data-testid="financials-print-btn"
        >
          {imprimiendo ? <><Loader2 size={13} className="animate-spin" /> Generando PDF...</> : <><Printer size={13} /> Imprimir PDF</>}
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3" data-testid="financials-kpis">
        <div className="bg-white rounded-2xl border border-gray-100 p-4" data-testid="kpi-costo-directo">
          <div className="flex items-center gap-1.5 mb-1">
            <Wallet size={12} className="text-[#2E6DA4]" />
            <p className="text-[9px] uppercase tracking-wider font-bold text-gray-500">Costo Directo</p>
          </div>
          <p className="text-base font-bold text-gray-900 tabular-nums">{fmtPyg(live.costo_directo)}</p>
          <p className="text-[10px] text-gray-400 mt-0.5">Mat + M.O.</p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-4" data-testid="kpi-utilidades">
          <div className="flex items-center gap-1.5 mb-1">
            <TrendingUp size={12} className="text-emerald-600" />
            <p className="text-[9px] uppercase tracking-wider font-bold text-gray-500">Utilidades</p>
          </div>
          <p className="text-base font-bold text-emerald-700 tabular-nums">{fmtPyg(live.utilidades)}</p>
          <p className="text-[10px] text-gray-400 mt-0.5">G.M + G.MO</p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-4" data-testid="kpi-gastos-admin">
          <div className="flex items-center gap-1.5 mb-1">
            <Percent size={12} className="text-purple-600" />
            <p className="text-[9px] uppercase tracking-wider font-bold text-gray-500">Gastos Admin.</p>
          </div>
          <p className="text-base font-bold text-purple-700 tabular-nums">{fmtPyg(live.ga)}</p>
          <p className="text-[10px] text-gray-400 mt-0.5">G.A.</p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-4" data-testid="kpi-margen">
          <div className="flex items-center gap-1.5 mb-1">
            <Target size={12} className="text-amber-600" />
            <p className="text-[9px] uppercase tracking-wider font-bold text-gray-500">Margen Neto</p>
          </div>
          <p className={`text-base font-bold tabular-nums ${live.ganancia_neta < 0 ? "text-red-600" : "text-amber-700"}`}>{live.margen_neto.toFixed(1)}%</p>
          <p className="text-[10px] text-gray-400 mt-0.5">(Utilidad - G.A.) / Subtotal</p>
          <p className="text-[10px] text-gray-500 mt-0.5 tabular-nums" data-testid="kpi-ganancia-neta">Ganancia Neta: {fmtPyg(live.ganancia_neta)}</p>
        </div>
        {/* CEN 2.0 FASE 2 sub-entrega 4 — Fix HE: ROI = Ganancia Neta / (Costo Directo + G.A.) */}
        <div className="bg-white rounded-2xl border border-gray-100 p-4" data-testid="kpi-roi">
          <div className="flex items-center gap-1.5 mb-1">
            <PieChartIcon size={12} className="text-rose-600" />
            <p className="text-[9px] uppercase tracking-wider font-bold text-gray-500">ROI</p>
          </div>
          <p className={`text-base font-bold tabular-nums ${live.ganancia_neta < 0 ? "text-red-600" : "text-rose-700"}`}>{live.roi.toFixed(1)}%</p>
          <p className="text-[10px] text-gray-400 mt-0.5">Ganancia Neta / Costo Total</p>
        </div>
        <div className="bg-gradient-to-br from-[#2E6DA4] to-[#1A4B7A] rounded-2xl p-4 text-white shadow-md" data-testid="kpi-total">
          <div className="flex items-center gap-1.5 mb-1">
            <CheckCircle2 size={12} />
            <p className="text-[9px] uppercase tracking-wider font-bold opacity-90">Total IVA inc.</p>
          </div>
          <p className="text-base font-bold tabular-nums">{fmtPyg(live.total)}</p>
          {live.descuento_activo ? (
            <p className="text-[10px] opacity-80 mt-0.5">Desc. -{live.descuento_porcentaje}%: -{fmtPyg(live.descuento_monto)} | IVA: {fmtPyg(live.iva)}</p>
          ) : (
            <p className="text-[10px] opacity-80 mt-0.5">IVA: {fmtPyg(live.iva)}</p>
          )}
        </div>
      </div>

      {/* Descuento banner (Feb 2026) */}
      {live.descuento_activo && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-4" data-testid="financials-descuento-banner">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="text-xs font-bold text-red-700 uppercase tracking-wide">
                Descuento aplicado sobre {live.descuento_aplica_sobre === "mano_de_obra" ? "Mano de Obra" : "Utilidades"}: -{live.descuento_porcentaje}%
              </p>
              <p className="text-[11px] text-red-600 mt-1">
                Base: {fmtPyg(live.descuento_base)} &rarr; Descuento: -{fmtPyg(live.descuento_monto)}
              </p>
            </div>
            <div className="text-right">
              <p className="text-[10px] text-gray-500">Subtotal sin desc.</p>
              <p className="text-sm font-bold text-gray-700 tabular-nums line-through">{fmtPyg(live.subtotal_neto)}</p>
              <p className="text-[10px] text-gray-500 mt-1">Subtotal con desc.</p>
              <p className="text-sm font-bold text-red-700 tabular-nums">{fmtPyg(live.subtotal_con_descuento)}</p>
            </div>
          </div>
        </div>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="financials-charts">
        {/* Pie distribution */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5" data-testid="chart-pie-distribution">
          <div className="flex items-center gap-2 mb-1">
            <PieChartIcon size={14} className="text-[#2E6DA4]" />
            <div>
              <h3 className="text-sm font-bold text-gray-800">Distribución del costo total</h3>
              <p className="text-[10px] text-gray-400">% del Subtotal Neto · {fmtPyg(live.subtotal)}</p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie
                isAnimationActive={!imprimiendo}
                data={distribution}
                dataKey="value"
                nameKey="name"
                innerRadius={50}
                outerRadius={90}
                paddingAngle={2}
                label={(entry) => `${((entry.value / live.subtotal) * 100).toFixed(0)}%`}
                labelLine={false}
              >
                {distribution.map((entry, i) => <Cell key={i} fill={entry.color} />)}
              </Pie>
              <Tooltip formatter={(value) => fmtPyg(value)} />
            </PieChart>
          </ResponsiveContainer>
          <div className="grid grid-cols-1 gap-1.5 mt-2">
            {distribution.map((d, i) => {
              const pctSub = live.subtotal > 0 ? (d.value / live.subtotal) * 100 : 0;
              return (
                <div key={i} className="flex items-center gap-1.5 text-xs">
                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: d.color }} />
                  <span className="text-gray-600 truncate flex-1">{d.name}</span>
                  <span className="text-gray-400 tabular-nums">{pctSub.toFixed(1)}%</span>
                  <span className="text-gray-700 font-semibold tabular-nums">{fmtPyg(d.value)}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Fix HE (2026-10-20): tortas de costo directo y de ganancias */}
        <TortaCard
          sinAnimacion={imprimiendo}
          testid="chart-pie-directo"
          titulo="Distribución del costo directo"
          subtitulo={`100% = Costo Directo · ${fmtPyg(live.costo_directo)}`}
          datos={distribucionDirecta}
          vacio="Sin costo directo para mostrar"
        />
        <TortaCard
          sinAnimacion={imprimiendo}
          testid="chart-pie-ganancias"
          titulo="Distribución de ganancias"
          subtitulo={`100% = Utilidades (G.M + G.MO) · ${fmtPyg(live.utilidades)}`}
          datos={distribucionGanancias}
          vacio="Sin ganancias para mostrar"
        />

        {/* Bar by section */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5" data-testid="chart-bar-sections">
          <div className="flex items-center gap-2 mb-3">
            <BarChart3 size={14} className="text-[#2E6DA4]" />
            <h3 className="text-sm font-bold text-gray-800">Costos por sección</h3>
          </div>
          {sectionData.length === 0 ? (
            <div className="text-center py-12 text-xs text-gray-400">Agregá secciones para ver el desglose</div>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={sectionData} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                <XAxis dataKey="name" tick={{ fontSize: 9 }} interval={0} angle={-15} textAnchor="end" height={50} />
                <YAxis tick={{ fontSize: 9 }} tickFormatter={(v) => v >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
                <Tooltip formatter={(v) => fmtPyg(v)} contentStyle={{ fontSize: 11 }} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                <Bar isAnimationActive={!imprimiendo} dataKey="Materiales" stackId="a" fill="#2E6DA4" />
                <Bar isAnimationActive={!imprimiendo} dataKey="Mano de Obra" stackId="a" fill="#F59E0B" />
                <Bar isAnimationActive={!imprimiendo} dataKey="Utilidades" stackId="a" fill="#10B981" />
                <Bar isAnimationActive={!imprimiendo} dataKey="G.A." stackId="a" fill="#8B5CF6" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* AI Analysis */}
      <div className="bg-gradient-to-br from-purple-50 via-white to-blue-50 rounded-2xl border border-purple-100 p-5" data-testid="ai-analysis-section">
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center">
              <Sparkles size={14} className="text-white" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-gray-800">Análisis con IA</h3>
              <p className="text-[11px] text-gray-500">Proyecciones de rentabilidad y recomendaciones (Gemini Flash)</p>
            </div>
          </div>
          <button
            onClick={runAiAnalysis}
            disabled={aiLoading || !budgetId}
            className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-lg px-4 py-2 text-xs font-semibold disabled:opacity-60 flex items-center gap-2"
            data-testid="ai-analyze-btn"
          >
            {aiLoading ? <><Loader2 size={13} className="animate-spin" /> Analizando...</> : <><Sparkles size={13} /> Analizar con IA</>}
          </button>
        </div>

        {!budgetId && (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 mt-2">
            ⚠ Guardá el presupuesto primero para poder analizarlo con IA.
          </div>
        )}

        {aiResult && (
          <div className="space-y-3 mt-3" data-testid="ai-result">
            {/* Rentabilidad */}
            {aiResult.analysis?.rentabilidad && (
              <div className="bg-white rounded-xl border border-gray-100 p-4">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                    aiResult.analysis.rentabilidad.calificacion === "Excelente" ? "bg-emerald-100 text-emerald-700" :
                    aiResult.analysis.rentabilidad.calificacion === "Buena" ? "bg-blue-100 text-blue-700" :
                    aiResult.analysis.rentabilidad.calificacion === "Aceptable" ? "bg-amber-100 text-amber-700" :
                    "bg-red-100 text-red-700"
                  }`}>{aiResult.analysis.rentabilidad.calificacion}</span>
                  <p className="text-xs font-bold text-gray-700">Rentabilidad</p>
                </div>
                <p className="text-sm text-gray-600">{aiResult.analysis.rentabilidad.comentario}</p>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
              {aiResult.analysis?.proyecciones?.length > 0 && (
                <div className="bg-white rounded-xl border border-blue-100 p-4">
                  <p className="text-[10px] uppercase tracking-wider font-bold text-blue-600 mb-2">📈 Proyecciones</p>
                  <ul className="space-y-1.5 text-xs text-gray-700">
                    {aiResult.analysis.proyecciones.map((p, i) => <li key={i} className="flex gap-1.5"><span>•</span><span>{p}</span></li>)}
                  </ul>
                </div>
              )}
              {aiResult.analysis?.recomendaciones?.length > 0 && (
                <div className="bg-white rounded-xl border border-emerald-100 p-4">
                  <p className="text-[10px] uppercase tracking-wider font-bold text-emerald-600 mb-2">💡 Recomendaciones</p>
                  <ul className="space-y-1.5 text-xs text-gray-700">
                    {aiResult.analysis.recomendaciones.map((r, i) => <li key={i} className="flex gap-1.5"><span>•</span><span>{r}</span></li>)}
                  </ul>
                </div>
              )}
              {aiResult.analysis?.alertas?.length > 0 && aiResult.analysis.alertas.some(a => a) && (
                <div className="bg-white rounded-xl border border-amber-100 p-4">
                  <p className="text-[10px] uppercase tracking-wider font-bold text-amber-600 mb-2">⚠ Alertas</p>
                  <ul className="space-y-1.5 text-xs text-gray-700">
                    {aiResult.analysis.alertas.filter(a => a).map((a, i) => <li key={i} className="flex gap-1.5"><span>•</span><span>{a}</span></li>)}
                  </ul>
                </div>
              )}
            </div>

            {aiResult.analysis?.kpis_clave && (
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-white rounded-xl border border-gray-100 p-3">
                  <p className="text-[9px] uppercase font-bold text-gray-500">Punto de equilibrio</p>
                  <p className="text-sm font-bold text-gray-800 tabular-nums">{fmtPyg(aiResult.analysis.kpis_clave.punto_equilibrio_estimado_pyg || 0)}</p>
                </div>
                <div className="bg-white rounded-xl border border-gray-100 p-3">
                  <p className="text-[9px] uppercase font-bold text-gray-500">Ratio Mat/MO</p>
                  <p className="text-sm font-bold text-gray-800">{aiResult.analysis.kpis_clave.ratio_mat_mo || "—"}</p>
                </div>
                <div className="bg-white rounded-xl border border-gray-100 p-3">
                  <p className="text-[9px] uppercase font-bold text-gray-500">Concentración riesgo</p>
                  <p className="text-sm font-bold text-gray-800">{aiResult.analysis.kpis_clave.concentracion_riesgo || "—"}</p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Resumen de Presupuesto (F1+F2+F3) */}
      <ResumenFinancieroSection budgetId={budgetId} items={items} pct={pct} budget={budget} readOnly={readOnly} precioInputMode={precioInputMode} />

      {/* Gastos Administrativos + Horas Hombre sections */}
      <GastosAdminSection budgetId={budgetId} initItems={budget?.gastos_admin_items || []} readOnly={readOnly} budget={budget} hhLive={hhLive} />
      <HorasHombreSection budgetId={budgetId} budget={budget} readOnly={readOnly} onLiveChange={handleHHLiveChange} />

      {/* Fix HF (2026-10-20): Simulador de Curva de Personal (Curva en S) */}
      <SimuladorCurvaPersonal budgetId={budgetId} budget={budget} readOnly={readOnly} hhLive={hhLive} />
    </div>
  );
}
